import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { ArticleContext } from '~/contexts/ArticleContext';
import Image from '~/components/Shared/Image/Image';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { shouldUseVideoPlayerAsCover } from '~/utilities/experiments/videoPlacement';

const cssClasses = cva('ml-1 pb-2 mt-4 md:m-3', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
    display: {
      true: '',
      false: 'hidden',
    },
  },
});

export default component$((): JSX.Element => {
  const {
    themeComponents: { image },
  } = useContext(AppDataContext);
  const isPremium = useIsPremiumTemplate();
  const article = useContext(ArticleContext);
  const { payloadToSSR } = useContext(AppDataContext);
  const { experiments, isMobileViewer } = payloadToSSR;

  if (!article || !article.cover) return <></>;

  let wrapperClass = '';

  let { aspectRatio, caption, credit, cropping } = article.cover.image.value;
  const { alt, path, fileExtension, host } = article.cover.image.value;
  // default cover image sizes
  let imageWidths = { small: 360, medium: 720, large: 1080 };

  if (isPremium) {
    wrapperClass = 'flex-1';
    imageWidths = { small: 360, medium: 720, large: 800 };
    cropping = {
      height: cropping?.height as number,
      width: cropping?.height as number,
      x: cropping?.x || 0,
      y: cropping?.y || 0,
    };
    aspectRatio = '1/1';
    caption = '';
    credit = '';
  }

  // TODO: Remove once the video placement experiment ends.
  const hasVideoPlayerAsCover = shouldUseVideoPlayerAsCover(
    article,
    isMobileViewer ?? false,
    experiments
  );
  if (hasVideoPlayerAsCover) wrapperClass = 'invisible';

  return (
    <Image
      alt={alt}
      aspectRatio={aspectRatio}
      path={path}
      caption={caption}
      credit={credit}
      cropping={cropping}
      host={host}
      fileExtension={fileExtension}
      wrapperClass={wrapperClass}
      captionClass={cssClasses({
        color: image.textColor.value,
        ...getFontSizeObject(image, 'fontSize'),
        display: !hasVideoPlayerAsCover,
      })}
      imageWidths={imageWidths}
      loading='eager'
    />
  );
});
